<template>
	<div
		class="telemetry-container"
		:class="{ 'headless-mode': headless }"
	>
		<header
			class="main-header"
			v-if="!headless"
		>
			<div class="header-top">
				<div class="device-info">
					<h1 class="device-title">
						{{ deviceName || "Device senza nome" }}
						<div class="status-wrapper">
							<StatusDot :value="computedStatus" />
							<span
								class="status-text"
								v-if="latestData"
							>
								{{ formatTimestamp(latestData?.timestamp) }}
								<span
									class="delay-badge"
									:class="getDelayClass(latestData?.timestamp)"
								>
									({{ getDelayLabel(latestData?.timestamp) }})
								</span>
							</span>
						</div>
					</h1>
					<div class="device-subtitle">
						<span class="device-id">ID: {{ deviceId }}</span>
						<span
							v-if="selectedVin"
							class="vin-display"
						>
							| VIN attivo: <code>{{ selectedVin }}</code>
						</span>
					</div>
				</div>

				<div class="header-actions">
					<ViewToggle
						id="toggle-button"
						class="toggle-icon"
					/>
					<NotificationBell
						placement="header"
						:deviceId="deviceId"
					/>
					<button
						id="exit-to-devices"
						class="btn-exit"
						@click="router.push('/devices')"
					>
						Esci
					</button>
				</div>
			</div>
			<div class="vin-selection">
				<div class="vin-selector">
					<div class="vin-label">
						<span>Seleziona Veicolo:</span>
					</div>
					<select
						v-model="selectedVin"
						@change="onVinChange"
						class="custom-select vin-select"
					>
						<option
							v-for="track in availableVins"
							:key="track.vin"
							:value="track.vin"
						>
							{{ track.vin }}
						</option>
					</select>
					<span class="vin-count">{{ availableVins.length }} veicoli</span>
				</div>

				<div class="vin-actions">
					<button
						class="btn-refresh"
						@click="fetchTelemetryData"
						:disabled="loading || !selectedVin"
					>
						<span
							class="refresh-icon"
							:class="{ spinning: loading }"
							>↻</span
						>
						Aggiorna
					</button>
				</div>
			</div>
			<!-- <div v-else class="no-vins-alert">
        ⚠️ Nessun veicolo registrato per questo dispositivo
      </div> -->
		</header>
		<!-- Selettore VIN SEMPRE visibile, sotto l'header di Data.vue -->
		<div
			class="vin-container"
			:class="{ 'headless-vin': headless }"
		>
			<div class="vin-selection">
				<div class="vin-selector">
					<div class="vin-label">
						<span>Seleziona Veicolo:</span>
					</div>
					<select
						v-model="selectedVin"
						@change="onVinChange"
						class="custom-select vin-select"
					>
						<option
							v-for="track in availableVins"
							:key="track.vin"
							:value="track.vin"
						>
							{{ track.vin }}
						</option>
					</select>
					<span class="vin-count">{{ availableVins.length }} veicoli</span>
				</div>
				<div class="vehicle-logs-wrapper">
				  <button
				    class="btn-logs"
				    @click="showVehicleLogs = !showVehicleLogs"
				    :disabled="!vehicleLogs.length"
				  >
				    Log veicolo
				    <span v-if="vehicleLogs.length" class="logs-badge">{{ vehicleLogs.length }}</span>
				  </button>
				
				  <div v-if="showVehicleLogs" class="vehicle-logs-popover">
				    <div class="vehicle-logs-header">
				      <span>Log disponibili</span>
				      <button class="btn-close-logs" @click="showVehicleLogs = false">✕</button>
				    </div>
					
				    <div v-if="vehicleLogs.length" class="vehicle-logs-list">
				      <div
				        v-for="logItem in vehicleLogs"
				        :key="logItem.id"
				        class="vehicle-log-item"
				      >
				        <div class="vehicle-log-top">
				          <span class="vehicle-log-vin">{{ logItem.vin }}</span>
				          <span class="vehicle-log-time">{{ formatDateDisplay(logItem.timestamp, true) }}</span>
				        </div>
				        <div class="vehicle-log-text">
				          {{ logItem.logs }}
				        </div>
				      </div>
				    </div>
					
				    <div v-else class="vehicle-logs-empty">
				      Nessun log disponibile
				    </div>
				  </div>
				</div>
				<div class="vin-actions">
					<button
						class="btn-refresh"
						@click="fetchTelemetryData"
						:disabled="loading || !selectedVin"
					>
						<span
							class="refresh-icon"
							:class="{ spinning: loading }"
							>↻</span
						>
						Aggiorna
					</button>
				</div>
			</div>
		</div>
		<!-- ▸ CONTENUTO PRINCIPALE -->
		<main class="telemetry-content">
			<!-- Sezione Panoramica -->
			<section class="section-block overview-section">
				<div class="overview-grid">
					<!-- Posizione (25%) -->
					<LocationCard
						v-if="chartVisibility.preferences.value.locationCard"
						class="span-1"
						:isEnabled="true"
						:deviceName="deviceName"
						:location="currentLocation"
						:address="currentAddress"
						@refresh="fetchTelemetryData"
						@center="centerOnMarker"
						@open-modal="openCoordModal"
					/>
					<!-- Stato Veicolo (50%) -->
					<div
						v-if="chartVisibility.preferences.value.vehicleStatus"
						class="card span-2 vehicle-status"
					>
						<div class="card-hdr">
							<h3>Stato Veicolo</h3>
						</div>
						<div class="status-grid">
							<div class="status-item">
								<span class="status-label">Velocità</span>
								<span class="status-value">{{ latestData?.vehicle_speed || 0 }} km/h</span>
							</div>
							<div class="status-item">
								<span class="status-label">RPM Motore</span>
								<span class="status-value">{{ latestData?.engine_rpm || 0 }}</span>
							</div>
							<div class="status-item">
								<span class="status-label">Temperatura</span>
								<span class="status-value">{{ latestData?.engine_temperature || 0 }}°C</span>
							</div>
							<div class="status-item">
								<span class="status-label">Carburante</span>
								<div class="fuel-gauge">
									<div class="fuel-bar">
										<div
											class="fuel-fill"
											:style="{ width: `${latestData?.fuel_level || 0}%` }"
										></div>
									</div>
									<span class="fuel-percentage">{{ latestData?.fuel_level || 0 }}%</span>
								</div>
							</div>
							<div class="status-item">
								<span class="status-label">Carico Motore</span>
								<span class="status-value">{{ latestData?.engine_load || 0 }}%</span>
							</div>
						</div>
					</div>

					<!-- Informazioni Veicolo (25%) -->
					<div class="card span-1 vehicle-info">
						<div class="card-hdr">
							<h3>Informazioni Veicolo</h3>
						</div>
						<div class="info-grid">
							<div class="info-item">
								<span class="info-label">VIN Selezionato</span>
								<span class="info-value">{{ selectedVin || "N/D" }}</span>
							</div>
							<div class="info-item">
								<span class="info-label">Ultimo Aggiornamento</span>
								<span class="info-value">{{ formatTimestamp(latestData?.timestamp) }}</span>
							</div>
							<div class="info-item">
								<span class="info-label">Record Totali</span>
								<span class="info-value">{{ stats.total_records || 0 }}</span>
							</div>
						</div>
					</div>

					<!-- Grafico Velocità RIMOSSO -->
				</div>
			</section>

			<section
				v-if="chartVisibility.preferences.value.historicalChart"
				class="dash-section charts-section"
			>
				<div class="section-header-main">
					<h2 class="telemetric-subtitle">Grafici Storici - {{ selectedVin }}</h2>
				</div>

				<div class="charts-tab charts-block">
					<div class="charts-header">
						<div class="chart-controls">
							<select
								v-model="selectedMetric"
								class="custom-select metric-select"
							>
								<option value="speed">Velocità</option>
								<option value="rpm">RPM Motore</option>
								<option value="temperature">Temperatura</option>
								<option value="fuel">Carburante</option>
								<option value="load">Carico Motore</option>
								<option value="battery">Batteria</option>
							</select>
							<div class="range-btn-group">
								<button
									class="range-btn"
									@click="setChartRange('1h')"
									:class="{ active: chartRange === '1h' }"
								>
									1h
								</button>
								<button
									class="range-btn"
									@click="setChartRange('24h')"
									:class="{ active: chartRange === '24h' }"
								>
									24h
								</button>
								<button
									class="range-btn"
									@click="setChartRange('7d')"
									:class="{ active: chartRange === '7d' }"
								>
									<span class="full-label">7d</span>
									<span class="short-label">7g</span>
								</button>
								<button
									class="range-btn"
									@click="setChartRange('30d')"
									:class="{ active: chartRange === '30d' }"
								>
									<span class="full-label">30d</span>
									<span class="short-label">30g</span>
								</button>
							</div>

							<div
								class="date-range-selector"
								style="position: relative"
							>
								<button
									class="date-range-trigger-btn"
									@click="showChartCalendar = !showChartCalendar"
									:title="getDateRangeTitle"
								>
									<span>{{ getDateRangeDisplay }}</span>
									<span class="icon">📅</span>
								</button>

								<!-- Calendar Popover per range -->
								<div
									v-if="showChartCalendar"
									class="calendar-popover-chart"
								>
									<div class="range-calendar-container">
										<!-- Calendar visuale per selezione range del grafico -->
										<MaterialCalendar
										  :selectedStartDate="startDate"
										  :selectedEndDate="endDate"
										  :activityDates="activityDates"
										  @range-select="onCalendarRangeSelect"
										  @close="showChartCalendar = false"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="chart-container">
						<HistoricalChart
							:metric="selectedMetric"
							:range="chartRange"
							:data="historicalData"
							:loading="historicalLoading"
							:vin="selectedVin"
							:timeRange="chartTimeRange"
						/>
					</div>
				</div>
			</section>

			<section
				v-if="chartVisibility.preferences.value.routeMap"
				class="dash-section map-section"
			>
				<div class="section-header-main">
					<h2 class="telemetric-subtitle">Percorso Veicolo</h2>
				</div>

        <div class="map-component-stacked">
          <RouteMap
					  :key="`route-map-${selectedVin}-${chartRange}-${startDate}-${endDate}`"
					  :locations="routeLocations"
					  :loading="historicalLoading"
					  :vin="selectedVin"
					  :startDate="startDate"
					  :endDate="endDate"
					  @date-change="onCalendarRangeSelect"
					/>
        </div>
      </section>

			<section
				v-if="chartVisibility.preferences.value.environmentSectionVehicle"
				class="dash-section environment-section"
			>
				<div class="section-header-main">
					<h2 class="telemetric-subtitle">ENVIRONMENT VALUES</h2>
					<button
						class="settings-btn-main"
						@click="showEnvConfig = true"
						title="Threshold Settings"
					>
						<FontAwesomeIcon :icon="faSliders" />
					</button>
				</div>
				<div
					class="environment-container"
					style="padding: 16px"
				>
					<EnvironmentSection
						:device-id="deviceId"
						:device-name="deviceName"
						:location="currentLocation"
						:current-temp-c="currentTempC || 0"
						:outside-hum="outsideHum || 0"
						:last-update-ago="lastUpdateAgo || 'N/D'"
						:chart-range="chartRangeMet || '24h'"
						:series-t="seriesT || []"
						:series-h="seriesH || []"
						:temp-min="tempMin"
						:temp-max="tempMax"
						:hum-min="humMin"
						:hum-max="humMax"
						:show-map="false"
						:loading="loading || meteoLoading"
						@refresh="emit('refresh')"
						@center="emit('center')"
						@copy-coords="emit('copy-coords')"
						@update:range="emit('update:range', $event)"
					/>
				</div>
			</section>

			<section
				v-if="chartVisibility.preferences.value.statisticsPanel"
				class="dash-section stats-section"
			>
				<div class="section-header-main">
					<h2 class="telemetric-subtitle">Statistiche e Analisi</h2>
				</div>
				<div class="stats-block">
					<StatisticsPanel
						:stats="stats"
						:latestData="latestData"
						:vin="selectedVin"
						:availableVins="availableVins"
					/>
				</div>
			</section>

			<!-- Config Modal per Ambiente -->
			<EnvironmentConfigModal
				v-if="showEnvConfig"
				:device-id="deviceId"
				@close="showEnvConfig = false"
				@saved="emit('refresh')"
			/>
		</main>
		<LocationModal
			v-if="showCoordModal"
			:deviceName="deviceName"
			:deviceId="deviceId"
			:location="currentLocation"
			:vin="selectedVin"
			@close="closeCoordModal"
			@copy="copyCoords"
			@saved="(loc) => { currentLocation = loc; closeCoordModal(); }"
		/>
	</div>
</template>

<script setup lang="ts">
	import axios from "@/axiosInstance";
	import HistoricalChart from "@/components/data/HistoricalChart.vue";
	import LocationCard from "@/components/data/LocationCard.vue";
	import RouteMap from "@/components/data/RouteMap.vue";
	import StatisticsPanel from "@/components/data/StatisticsPanel.vue";
	import NotificationBell from "@/components/NotificationBell.vue";
	import StatusDot from "@/components/ui/StatusDot.vue";
	import ViewToggle from "@/components/ViewToggle.vue";
	import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
	import { useRoute, useRouter } from "vue-router";
	import { useToast } from "vue-toast-notification";
	import LocationModal from "./data/LocationModal.vue";
	import MaterialCalendar from "./ui/MaterialCalendarRange.vue";

	import EnvironmentConfigModal from "@/components/data/EnvironmentConfigModal.vue";
	import EnvironmentSection from "@/components/data/EnvironmentSection.vue";
	import { useChartVisibility } from "@/composables/useChartVisibility";

	import { faSliders } from "@fortawesome/free-solid-svg-icons";
	import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

	const route = useRoute();
	const router = useRouter();
	const toast = useToast();

	const showEnvConfig = ref(false);

	// Chart visibility
	const chartVisibility = useChartVisibility();

	// Props e stati
	const props = defineProps<{
		headless?: boolean;
		// Environment Props passed from Data.vue
		currentTempC?: number;
		outsideHum?: number;
		lastUpdateAgo?: string;
		chartRangeMet?: string; // Differenziare dal chartRange interno (speed)
		seriesT?: any[];
		seriesH?: any[];
		tempMin?: number;
		tempMax?: number;
		humMin?: number;

		humMax?: number;
		meteoLoading?: boolean;
	}>();

	const emit = defineEmits(["refresh", "center", "copy-coords", "update:range", "location-update", "timestamp-update"]);

	const deviceId = computed(() => {
		const val = route.params.deviceId;
		const n = Number(val);
		return Number.isNaN(n) ? 0 : n;
	});

	const deviceName = ref("");
	const currentAddress = ref("");
	const lastGeocodedCoords = ref<string>("");
	const apiStatus = ref<"online" | "offline" | "maintenance" | "error">("offline");
	const loading = ref(false);
	const historicalLoading = ref(false); // loading separato per dati storici
	const showCoordModal = ref(false);
	const showChartCalendar = ref(false);
	const vehicleLogs = ref<any[]>([]);
	const showVehicleLogs = ref(false);

	// VIN Management
	const selectedVin = ref<string>("");
	const availableVins = ref<Array<{ vin: string }>>([]);

	// Dati
	const latestData = ref<any>(null);
	// chartData rimosso
	const historicalData = ref<any[]>([]); // Per grafici storici (basato su range)
	const routeLocations = ref<any[]>([]);
	const historicalRouteLocations = ref<any[]>([]); // <-- NUOVA variabile

	const stats = ref({
		total_records: 0,
		unique_vehicles: 0,
		latest_timestamp: 0,
		earliest_timestamp: 0,
	});

	// Configurazione grafici
	const selectedMetric = ref("speed");
	const chartRange = ref("24h");
	const chartTimeRange = ref<{ start: number; end: number } | null>(null);
	const startDate = ref<string>(""); // YYYY-MM-DD
	const endDate = ref<string>(""); // YYYY-MM-DD
	// const selectedDate = ref<string>(""); // YYYY-MM-DD

	// function onChartDateSelect(date: string) {
	//   selectedDate.value = date;
	//   showChartCalendar.value = false;
	//   endDate.value = date;
	//   // Solo per route/mappa, NON per environment charts
	//   chartRange.value = "custom";
	//   fetchHistoricalData("custom", true); // Questo carica solo i dati del percorso

	//   toast.info(`Percorso aggiornato fino al: ${formatDateDisplay(date)}`, {
	//     position: "top-right",
	//     duration: 3000,
	//   });
	// }

	// function onChartDateSelect(date: string) {
	//   // RouteMap seleziona un giorno → lo traduciamo in range custom di 1 giorno
	//   startDate.value = date;
	//   endDate.value = date;
	//   chartRange.value = "custom";
	//   showChartCalendar.value = false;

	//   refreshTimeWindow("custom");

	//   toast.info(`Percorso e grafico aggiornati al: ${formatDateDisplay(date)}`, {
	//     position: "top-right",
	//     duration: 3000,
	//   });
	// }

	function computeTimeWindow(range: string, endTimestampFallback?: number) {
	  const endTime = endTimestampFallback || Math.floor(Date.now() / 1000);
	  let startTime = 0;

	  if (range === "custom" && startDate.value && endDate.value) {
	    const s = new Date(startDate.value);
	    s.setHours(0, 0, 0, 0);
	    const e = new Date(endDate.value);
	    e.setHours(23, 59, 59, 999);

	    return {
	      startTime: Math.floor(s.getTime() / 1000),
	      endTime: Math.floor(e.getTime() / 1000),
	    };
	  }

	  switch (range) {
	    case "1h":
	      startTime = endTime - 60 * 60;
	      break;
	    case "24h":
	      startTime = endTime - 24 * 60 * 60;
	      break;
	    case "7d":
	      startTime = endTime - 7 * 24 * 60 * 60;
	      break;
	    case "30d":
	      startTime = endTime - 30 * 24 * 60 * 60;
	      break;
	    default:
	      startTime = endTime - 24 * 60 * 60;
	  }

	  return { startTime, endTime };
	}

	// Computed per display
	const getDateRangeDisplay = computed(() => {
		if (!startDate.value && !endDate.value) {
			return "Seleziona Data";
		}
		if (startDate.value === endDate.value) {
			return formatDateDisplay(startDate.value);
		}
		return `${formatDateDisplay(startDate.value)} - ${formatDateDisplay(endDate.value)}`;
	});

	const getDateRangeTitle = computed(() => {
		if (!startDate.value && !endDate.value) {
			return "Seleziona un range di date";
		}
		return `Dal ${startDate.value} al ${endDate.value}`;
	});

	const isDateRangeValid = computed(() => {
		if (!startDate.value || !endDate.value) return false;

		const start = new Date(startDate.value);
		const end = new Date(endDate.value);
		return start <= end;
	});

	// TelemetryPage.vue
	// const filteredRouteLocations = computed(() => {
	// 	// Se non ci sono date di range, mostra tutti i dati
	// 	if (!startDate.value || !endDate.value) {
	// 		return routeLocations.value;
	// 	}

	// 	// Converti date in timestamp
	// 	const startDateObj = new Date(startDate.value);
	// 	startDateObj.setHours(0, 0, 0, 0);
	// 	const startTimestamp = Math.floor(startDateObj.getTime() / 1000);

	// 	const endDateObj = new Date(endDate.value);
	// 	endDateObj.setHours(23, 59, 59, 999);
	// 	const endTimestamp = Math.floor(endDateObj.getTime() / 1000);

	// 	// Filtra i locations
	// 	// Filtra i locations e conta
	// 	const filtered = routeLocations.value.filter((loc) => {
	// 		const timestamp = typeof loc.timestamp === "string" ? parseInt(loc.timestamp, 10) : loc.timestamp;

	// 		return timestamp >= startTimestamp && timestamp <= endTimestamp;
	// 	});

	// 	// DEBUG: Log dei risultati
	// 	// console.log('[DEBUG] filteredRouteLocations:', {
	// 	//   dateRange: `${startDate.value} - ${endDate.value}`,
	// 	//   timestampRange: `${startTimestamp} - ${endTimestamp}`,
	// 	//   totalLocations: routeLocations.value.length,
	// 	//   filteredCount: filtered.length,
	// 	//   percentage: routeLocations.value.length > 0 ?
	// 	//     Math.round((filtered.length / routeLocations.value.length) * 100) + '%' : '0%'
	// 	// });

	// 	// // DEBUG dettagliato: Mostra i primi 3 timestamps
	// 	// if (filtered.length > 0) {
	// 	//   console.log('[DEBUG] Sample filtered timestamps:',
	// 	//     filtered.slice(0, 3).map(loc => ({
	// 	//       timestamp: loc.timestamp,
	// 	//       date: new Date(
	// 	//         (typeof loc.timestamp === 'string' ?
	// 	//          parseInt(loc.timestamp, 10) : loc.timestamp) * 1000
	// 	//       ).toISOString()
	// 	//     }))
	// 	//   );
	// 	// }

	// 	return filtered;
	// });

	function extractVehicleLogs(logs: any[]) {
	  if (!Array.isArray(logs)) return [];
		
	  return logs
	    .filter((item: any) => {
	      const hasTimestamp = item?.timestamp != null;
	      const hasLogText =
	        item?.logs !== null &&
	        item?.logs !== undefined &&
	        String(item.logs).trim() !== "";
			
	      return hasTimestamp && hasLogText;
	    })
	    .map((item: any) => ({
	      id: item.id,
	      vin: item.vin,
	      timestamp: Number(item.timestamp),
	      logs: String(item.logs).trim(),
	    }))
	    .sort((a, b) => b.timestamp - a.timestamp);
	}

	// Helper function
	function formatDateDisplay(dateStr: string, forLogs?: boolean): string {
		if (!dateStr) return "";
		const date = new Date(dateStr);
		if (forLogs) {
			const ts = typeof dateStr === "string" ? parseInt(dateStr, 10) : dateStr;
  		if (isNaN(ts)) return "N/D";
			return new Date(ts * 1000).toLocaleString("it-IT", {
  		  day: "2-digit",
  		  month: "2-digit",
  		  year: "numeric",
  		  hour: "2-digit",
  		  minute: "2-digit",
  		});
		}
		return date.toLocaleDateString("it-IT", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	}

	const activityDates = computed(() => {
		// 1. Always include selectedDate if present
		const dates = new Set<string>();
		if (startDate.value) dates.add(startDate.value);
		if (endDate.value) dates.add(endDate.value);
		// 2. Derive dates from loaded historicalData (for ranges like 7d/30d)
		// This will show which days have data in the calendar
		if (historicalData.value && historicalData.value.length > 0) {
			historicalData.value.forEach((d) => {
				if (d.timestamp) {
					// Convert timestamp (seconds) to YYYY-MM-DD
					const dateStr = new Date(d.timestamp * 1000).toISOString().split("T")[0];
					dates.add(dateStr);
				}
			});
		}

		// 3. Also check routeLocations for current loaded route
		if (routeLocations.value && routeLocations.value.length > 0) {
			routeLocations.value.forEach((loc) => {
				if (loc.timestamp) {
					const dateStr = new Date(loc.timestamp * 1000).toISOString().split("T")[0];
					dates.add(dateStr);
				}
			});
		}

		return Array.from(dates);
	});

	// Computed
	const currentLocation = computed<[number, number] | undefined>(() => {
		if (latestData.value?.latitude && latestData.value?.longitude) {
			return [latestData.value.latitude, latestData.value.longitude] as [number, number];
		}
		return undefined;
	});

	// Fetch device data con tracks
	async function fetchDeviceData() {
		try {
			if (!deviceId.value) return;
			loading.value = true;
			const deviceRes = await axios.get(`/devices/${deviceId.value}/get-graph`);
			console.log("DeviceRes data:", deviceRes.data);

			deviceName.value = deviceRes.data?.name || "";
			apiStatus.value = deviceRes.data?.status || "offline";

			// Estrai la lista dei tracks/VIN
			const tracks = deviceRes.data?.track_sat_data || [];
			availableVins.value = tracks.map((track: any) => ({
				vin: track.vin,
			}));

			// console.log("VIN disponibili:", availableVins.value);
			// console.log("VIN selezionat numerico:", selectedVin.value.length);
			// console.log("Selezionato VIN:", selectedVin.value);

			// Seleziona il primo VIN se disponibile e non ne è già selezionato uno
			if (availableVins.value.length > 0 && !selectedVin.value) {
				selectedVin.value = availableVins.value[0].vin;
				// console.log("Selezionato VIN di default:", selectedVin.value);
				await fetchTelemetryData();
				// await fetchHistoricalData(); // <-- Carica anche i dati storici iniziali
				await refreshTimeWindow();
			}
		} catch (error) {
			console.error("Errore fetch dati device:", error);
			toast.error("Errore nel caricamento dati dispositivo", {
				position: "top-right",
			});
		} finally {
			loading.value = false;
		}
	}

	// Fetch dati telemetria PER OVERVIEW (ultimi 100 record)
	async function fetchTelemetryData() {
		if (!selectedVin.value) {
			latestData.value = null;
			toast.warning("Seleziona un VIN per visualizzare i dati", {
				position: "top-right",
			});
			return;
		}

		loading.value = true;
		//console.log(
		//  "[TelemetryPage] fetchTelemetryData started for VIN:",
		//  selectedVin.value
		//);

		try {
			// 1. Fetch ultimi 100 record per overview
			// console.log("[TelemetryPage] Fetching latest logs...");
			if (!deviceId.value) return;
			// console.log("deviceId:", deviceId.value);
			// console.log("selectedVin:", selectedVin.value);
			const chartRes = await axios.get(`/track/${deviceId.value}/logs`, {
				params: {
					limit: 100,
					vin: selectedVin.value,
					orderBy: "timestamp",
					orderDirection: "DESC",
				},
			});
			// console.log("ChartRes data:", chartRes.data);

			//console.log(
			//  "[TelemetryPage] Logs response:",
			//  chartRes.data?.logs?.length,
			//  "logs"
			//);

			if (chartRes.data?.logs?.length) {
				// Filtra record con dati nulli
				const filteredLogs = chartRes.data.logs.filter((log: any) => {
					const hasData = log.vehicle_speed !== null || log.engine_rpm !== null || log.fuel_level !== null || log.engine_temperature !== null || log.tracker_battery !== null || (log.latitude !== null && log.longitude !== null);
					// console.log("[TelemetryPage] Log", log.timestamp, "has data:", hasData);
					return hasData;
				});

				// console.log(
				//   "[TelemetryPage] Filtered logs:",
				//   filteredLogs.length,
				//   "valid logs"
				// );

				if (filteredLogs.length > 0) {
					// chartData.value = filteredLogs; // Rimosso

					// Prepara locations per la mappa
					const logs = filteredLogs.length > 0 ? filteredLogs : chartRes.data.logs;

					// Aggiorna latestData
					latestData.value = logs[0];
					console.log("Latest data:", latestData.value);

					// Aggiorna routeLocations per la mappa
					// Mappatura dati
					// const newRouteLocations = logs.map((d: any) => ({
					// 	lat: d.latitude,
					// 	lng: d.longitude,
					// 	timestamp: d.timestamp,
					// 	speed: d.vehicle_speed,
					// 	rpm: d.engine_rpm,
					// 	fuel: d.fuel_level,
					// 	vin: d.vin,
					// }));

					// Ordina ASC (Oldest -> Newest) per la mappa
					// newRouteLocations.reverse();

					// LOGICA PRESERVAZIONE STORICO:
					// Se abbiamo caricato molti punti (es. > 200, storico giornaliero) e l'aggiornamento è parziale (es. 100 punti del polling),
					// NON sovrascrivere tutto ma aggiungi solo i punti nuovi in coda.
					// if (routeLocations.value.length > 200 && newRouteLocations.length <= 150) {
					// 	const lastCurrent = routeLocations.value[routeLocations.value.length - 1];
					// 	const lastTs = typeof lastCurrent.timestamp === "string" ? parseInt(lastCurrent.timestamp) : lastCurrent.timestamp;

					// 	// Trova punti in newRouteLocations che sono successivi all'ultimo punto attuale
					// 	const freshPoints = newRouteLocations.filter((p: any) => {
					// 		const pts = typeof p.timestamp === "string" ? parseInt(p.timestamp) : p.timestamp;
					// 		return pts > lastTs;
					// 	});

					// 	if (freshPoints.length > 0) {
					// 		// console.log(
					// 		//   `[TelemetryPage] Appending ${freshPoints.length} new points to history`
					// 		// );
					// 		routeLocations.value.push(...freshPoints);
					// 	} else {
					// 		// console.log('[TelemetryPage] Preserving history, no new points');
					// 	}
					// } else {
					// 	// Comportamento standard: rimpiazza SOLO i punti live
					// 	// Mantieni historicalRouteLocations se esistono
					// 	const freshLivePoints = newRouteLocations; // questi sono i nuovi punti live

					// 	// Mantieni historical + aggiungi nuovi live
					// 	routeLocations.value = [...historicalRouteLocations.value, ...freshLivePoints.filter((newPoint: any) => !historicalRouteLocations.value.some((histPoint) => histPoint.timestamp === newPoint.timestamp))];
					// }

					// Emetti la location al componente padre per i dati meteo
					// if (routeLocations.value.length > 0) {
					// 	const lastLoc = routeLocations.value[routeLocations.value.length - 1]; // Usa l'ultimo punto per il meteo live
					// 	if (lastLoc.lat && lastLoc.lng) {
					// 		emit("location-update", [lastLoc.lat, lastLoc.lng]);
					// 	}
					// }
					if (latestData.value?.latitude != null && latestData.value?.longitude != null) {
					  emit("location-update", [latestData.value.latitude, latestData.value.longitude]);
					}

					// Emetti il timestamp al componente padre per l'header
					if (latestData.value?.timestamp) {
						emit("timestamp-update", latestData.value.timestamp);
					}

					// console.log(
					//   "[TelemetryPage] Route locations loaded:",
					//   routeLocations.value.length,
					//   "points"
					// );

					// Reverse Geocoding se coordinate sono cambiate
					const coordsKey = `${latestData.value.latitude},${latestData.value.longitude}`;
					if (coordsKey !== lastGeocodedCoords.value) {
						fetchAddress(latestData.value.latitude, latestData.value.longitude);
						lastGeocodedCoords.value = coordsKey;
					}

					// console.log("[TelemetryPage] Route locations updated");
				} else {
					// console.log("[TelemetryPage] No valid logs found");
					latestData.value = null;
					// chartData.value = [];
					routeLocations.value.length = 0;
				}
			}

			// 2. Fetch statistiche per VIN specifico
			// console.log("[TelemetryPage] Fetching stats...");
			const statsRes = await axios.get(`/track/${deviceId.value}/logs/stats`, {
				params: { vin: selectedVin.value },
			});

			if (statsRes.data?.stats) {
				stats.value = {
					...statsRes.data.stats,
					unique_vehicles: 1,
				};
				// console.log("[TelemetryPage] Stats loaded");
			}
		} catch (error) {
			console.error("[TelemetryPage] Error fetching telemetry data:", error);
			toast.error("Errore nel caricamento dati telemetria", {
				position: "top-right",
			});
		} finally {
			loading.value = false;
			// console.log("[TelemetryPage] fetchTelemetryData completed");
		}
	}

	// Fetch dati storici per range
	// async function fetchHistoricalData(rangeOverride?: string) {
	//   const range = rangeOverride || chartRange.value;

	//   if (!selectedVin.value) {
	//     historicalData.value = [];
	//     return;
	//   }

	//   historicalLoading.value = true;

	//   try {
	//     // 1. DECLARA startTime e endTime
	//     let startTime = 0;
	//     let endTime = Math.floor(Date.now() / 1000); // Default: ora corrente

	//     // 2. CALCOLA in base al range - SOLO UNA VOLTA
	//     if (range === "custom" && selectedDate.value) {
	//       // Custom range con data specifica
	//       const dateObj = new Date(selectedDate.value);
	//       dateObj.setHours(0, 0, 0, 0);
	//       startTime = Math.floor(dateObj.getTime() / 1000);

	//       const endDateObj = new Date(selectedDate.value);
	//       endDateObj.setHours(23, 59, 59, 999);
	//       endTime = Math.floor(endDateObj.getTime() / 1000);
	//     } else {
	//       // Preset ranges (1h, 24h, 7d, 30d) - usa ora corrente come riferimento
	//       switch (range) {
	//         case "1h":
	//           startTime = endTime - 60 * 60;
	//           break;
	//         case "24h":
	//           startTime = endTime - 24 * 60 * 60;
	//           break;
	//         case "7d":
	//           startTime = endTime - 7 * 24 * 60 * 60;
	//           break;
	//         case "30d":
	//           startTime = endTime - 30 * 24 * 60 * 60;
	//           break;
	//         default:
	//           startTime = endTime - 24 * 60 * 60; // default 24h
	//       }
	//     }

	//     // 3. DEBUG LOG (opzionale)
	//     console.log("[TelemetryPage] Historical timeframe:", {
	//       range,
	//       startTime,
	//       endTime,
	//       startDate: new Date(startTime * 1000).toISOString(),
	//       endDate: new Date(endTime * 1000).toISOString(),
	//       rangeSeconds: endTime - startTime,
	//     });

	//     // 4. CHIAMATA API
	//     const historicalRes = await axios.get(`/track/${deviceId.value}/logs`, {
	//       params: {
	//         limit: range === "custom" ? 2000 : 500,
	//         vin: selectedVin.value,
	//         startTime: startTime.toString(),
	//         endTime: endTime.toString(),
	//         orderBy: "timestamp",
	//         orderDirection: "ASC",
	//       },
	//     });

	//     // Modifica la parte del debug (riga dopo la chiamata axios):
	//     console.log(`[DEBUG] Range: ${range}`);
	//     console.log(
	//       `[DEBUG] Record ricevuti: ${historicalRes.data?.logs?.length || 0}`
	//     );

	//     if (historicalRes.data?.logs?.length) {
	//       console.log(
	//         `[DEBUG] Primo timestamp: ${
	//           historicalRes.data.logs[0].timestamp
	//         } (${new Date(
	//           historicalRes.data.logs[0].timestamp * 1000
	//         ).toISOString()})`
	//       );
	//       console.log(
	//         `[DEBUG] Ultimo timestamp: ${
	//           historicalRes.data.logs[historicalRes.data.logs.length - 1].timestamp
	//         } (${new Date(
	//           historicalRes.data.logs[historicalRes.data.logs.length - 1]
	//             .timestamp * 1000
	//         ).toISOString()})`
	//       );

	//       const first = historicalRes.data.logs[0].timestamp;
	//       const last =
	//         historicalRes.data.logs[historicalRes.data.logs.length - 1].timestamp;
	//       const daysCovered = (last - first) / 86400;
	//       console.log(`[DEBUG] Giorni coperti nei dati: ${daysCovered.toFixed(2)}`);
	//     }

	//     // 5. PROCESSO DATI (mantieni il resto uguale...)
	//     if (historicalRes.data?.logs?.length) {
	//       // Filtra e processa i dati per i grafici
	//       const filteredHistoricalLogs = historicalRes.data.logs.filter(
	//         (log: any) => {
	//           return (
	//             log.timestamp &&
	//             (log.vehicle_speed !== null ||
	//               log.engine_rpm !== null ||
	//               log.fuel_level !== null ||
	//               log.engine_temperature !== null ||
	//               log.engine_load !== null)
	//           );
	//         }
	//       );

	//       if (filteredHistoricalLogs.length > 0) {
	//         const sortedLogs = [...filteredHistoricalLogs].sort((a, b) => {
	//           const tsA =
	//             typeof a.timestamp === "string"
	//               ? parseInt(a.timestamp, 10)
	//               : a.timestamp;
	//           const tsB =
	//             typeof b.timestamp === "string"
	//               ? parseInt(b.timestamp, 10)
	//               : b.timestamp;
	//           return tsA - tsB;
	//         });

	//         historicalData.value = sortedLogs;

	//         // AGGIORNAMENTO ROTTA
	//         const newRouteLocations = sortedLogs
	//           .filter((d: any) => d.latitude && d.longitude)
	//           .map((d: any) => ({
	//             lat: d.latitude,
	//             lng: d.longitude,
	//             timestamp: d.timestamp,
	//             speed: d.vehicle_speed,
	//             rpm: d.engine_rpm,
	//             fuel: d.fuel_level,
	//             vin: d.vin,
	//           }));

	//         if (newRouteLocations.length > 0) {
	//           historicalRouteLocations.value = newRouteLocations;

	//           // Combina: storici + punti live (che non sono già negli storici)
	//           const freshPoints = getFreshLivePoints();
	//           routeLocations.value = [
	//             ...historicalRouteLocations.value,
	//             ...freshPoints,
	//           ];

	//           console.log(
	//             `[TelemetryPage] Combined route: ${historicalRouteLocations.value.length} historical + ${freshPoints.length} live points`
	//           );
	//         }
	//       } else {
	//         historicalData.value = [];
	//       }
	//     } else {
	//       // Fallback: prova a recuperare TUTTI i dati del VIN
	//       const allDataRes = await axios.get(`/track/${deviceId.value}/logs`, {
	//         params: {
	//           limit: 500,
	//           vin: selectedVin.value,
	//           orderBy: "timestamp",
	//           orderDirection: "ASC",
	//         },
	//       });

	//       if (allDataRes.data?.logs?.length) {
	//         historicalData.value = allDataRes.data.logs;
	//       } else {
	//         historicalData.value = [];
	//       }
	//     }
	//   } catch (error) {
	//     console.error("[TelemetryPage] Error fetching historical data:", error);
	//     toast.error("Errore nel caricamento dati storici", {
	//       position: "top-right",
	//     });
	//     historicalData.value = [];
	//   } finally {
	//     historicalLoading.value = false;
	//   }
	// }
	async function fetchLastTimestampFromDB(): Promise<number | null> {
	  try {
	    const lastRecordRes = await axios.get(`/track/${deviceId.value}/logs`, {
	      params: {
	        vin: selectedVin.value,
	        limit: 1,
	        orderBy: "timestamp",
	        orderDirection: "DESC",
	      },
	    });

	    const ts = lastRecordRes.data?.logs?.[0]?.timestamp;
	    return ts ? Number(ts) : null;
	  } catch (err) {
	    console.warn("[DEBUG] Could not fetch last timestamp:", err);
	    return null;
	  }
	}

	async function fetchChart(rangeOverride?: string, lastTs?: number | null) {
	  if (!selectedVin.value || !deviceId.value) {
	    historicalData.value = [];
	    return;
	  }

	  const range = rangeOverride || chartRange.value;

	  // const lastTs = await fetchLastTimestampFromDB();
	  const { startTime, endTime } = computeTimeWindow(range, lastTs ?? undefined);

	  const metricMap: Record<string, string> = {
	    speed: "vehicle_speed",
	    rpm: "engine_rpm",
	    temperature: "engine_temperature",
	    fuel: "fuel_level",
	    load: "engine_load",
	    battery: "tracker_battery",
	  };
	  const metric = metricMap[selectedMetric.value] || "vehicle_speed";

	  const maxPointsByRange: Record<string, number> = {
	    "1h": 1000,
	    "24h": 2880,
	    "7d": 3360,
	    "30d": 3600,
	    custom: 1500,
	  };
	  const maxPoints = maxPointsByRange[range] ?? 500;

	  const shouldDownsample = ["7d", "30d", "custom"].includes(range);

	  const res = await axios.get(`/track/${deviceId.value}/logs`, {
	    params: {
	      vin: selectedVin.value,
	      orderBy: "timestamp",
	      orderDirection: "ASC",
	      startTime: String(startTime),
	      endTime: String(endTime),
	      downsample: shouldDownsample ? "true" : "false",
	      maxPoints,
	      metric,
	      strategy: "graph",
	      limit: shouldDownsample ? 50000 : 5000,
	    },
	  });
		
		const metaTR = res.data?.metadata?.timeRange;

		// se l'API te lo manda, usa quello
		if (metaTR?.start != null && metaTR?.end != null) {
		  chartTimeRange.value = { start: Number(metaTR.start), end: Number(metaTR.end) };
		} else {
		  // fallback: usa la finestra calcolata client-side
		  chartTimeRange.value = { start: startTime, end: endTime };
		}

	  const logs = res.data?.logs || [];
	  historicalData.value = logs.filter((l: any) => l?.timestamp != null);
		vehicleLogs.value = extractVehicleLogs(logs);
	}

	async function fetchRoute(rangeOverride?: string, lastTs?: number | null) {
	  if (!selectedVin.value || !deviceId.value) {
	    routeLocations.value = [];
	    return;
	  }

	  const range = rangeOverride || chartRange.value;

	  // const lastTs = await fetchLastTimestampFromDB();
	  const { startTime, endTime } = computeTimeWindow(range, lastTs ?? undefined);

	  // Max points rotta: puoi tenerli alti, la mappa regge ~1k-2k senza morire
	  const routeMaxPointsByRange: Record<string, number> = {
	    "1h": 400,
	    "24h": 800,
	    "7d": 1200,
	    "30d": 1500,
	    custom: 1200,
	  };
	  const maxPoints = routeMaxPointsByRange[range] ?? 800;

	  const shouldDownsample = ["7d", "30d", "custom"].includes(range);

	  const res = await axios.get(`/track/${deviceId.value}/logs`, {
	    params: {
	      vin: selectedVin.value,
	      orderBy: "timestamp",
	      orderDirection: "ASC",
	      startTime: String(startTime),
	      endTime: String(endTime),
	      downsample: shouldDownsample ? "true" : "false",
	      maxPoints,
	      strategy: "route",
	      limit: shouldDownsample ? 50000 : 5000,
	    },
	  });

	  const logs = res.data?.logs || [];
		console.log("veri logs", res.data);

	  routeLocations.value = logs
	    .filter((d: any) => d?.latitude != null && d?.longitude != null && d?.timestamp != null)
	    .map((d: any) => ({
	      lat: d.latitude,
	      lng: d.longitude,
	      timestamp: d.timestamp,
	      speed: d.vehicle_speed,
	      rpm: d.engine_rpm,
	      fuel: d.fuel_level,
	      vin: d.vin,
	    }));
	}

	async function refreshTimeWindow(rangeOverride?: string) {
	  const r = rangeOverride || chartRange.value;
	  historicalLoading.value = true;
	  try {
			const lastTs = await fetchLastTimestampFromDB();
	    await Promise.all([fetchChart(r, lastTs), fetchRoute(r, lastTs)]);
	  } finally {
	    historicalLoading.value = false;
	  }
	}

	// async function fetchHistoricalData(rangeOverride?: string, isSingleDateMode?: Boolean) {
	// 	const range = rangeOverride || chartRange.value;
	// 	console.log("range in fetchHistoricalData:", range);

	// 	if (!selectedVin.value) {
	// 		historicalData.value = [];
	// 		return;
	// 	}

	// 	historicalLoading.value = true;

	// 	try {
	// 		// 1. PRIMA recupera l'ultimo timestamp disponibile per questo VIN
	// 		let lastTimestampFromDB = null;
	// 		try {
	// 			const lastRecordRes = await axios.get(`/track/${deviceId.value}/logs`, {
	// 				params: {
	// 					vin: selectedVin.value,
	// 					limit: 1,
	// 					orderBy: "timestamp",
	// 					orderDirection: "DESC",
	// 				},
	// 			});
	// 			console.log("lastrecordRes data:", lastRecordRes.data?.logs?.[0]?.timestamp);

	// 			if (lastRecordRes.data?.logs?.[0]?.timestamp) {
	// 				lastTimestampFromDB = lastRecordRes.data.logs[0].timestamp;
	// 				console.log(`[DEBUG] Last timestamp from DB:`, {
	// 				  timestamp: lastTimestampFromDB,
	// 				  date: new Date(lastTimestampFromDB * 1000).toISOString()
	// 				});
	// 			}
	// 		} catch (err) {
	// 			console.warn("[DEBUG] Could not fetch last timestamp:", err);
	// 		}

	// 		// 2. CALCOLA startTime e endTime
	// 		let startTime = 0;
	// 		let endTime = lastTimestampFromDB || Math.floor(Date.now() / 1000); // Usa DB timestamp se disponibile

	// 		// 2. CALCOLA in base al range - SOLO UNA VOLTA
	// 		if (range === "custom") {
  //       console.log("arrivo in range custom con date:? e sono presenti startdate.value e enddate.value?", startDate.value, endDate.value);
	// 			// RANGE PERSONALIZZATO con due date
	// 			if (endDate.value) {
	// 				const endDateObj = new Date(endDate.value);
	// 				endDateObj.setHours(23, 59, 59, 999);
	// 				endTime = Math.floor(endDateObj.getTime() / 1000);
	// 				if (!isSingleDateMode) {
	// 					const startDateObj = new Date(startDate.value);
	// 					startDateObj.setHours(0, 0, 0, 0);
	// 					startTime = Math.floor(startDateObj.getTime() / 1000);
	
	// 					const endDateObj = new Date(endDate.value);
	// 					endDateObj.setHours(23, 59, 59, 999);
	// 					endTime = Math.floor(endDateObj.getTime() / 1000);
	
	// 					// console.log(`[Custom Range] Dal ${startDate.value} al ${endDate.value}`, {
	// 					//   startTime,
	// 					//   endTime,
	// 					//   startDate: new Date(startTime * 1000).toISOString(),
	// 					//   endDate: new Date(endTime * 1000).toISOString()
	// 					// });
	// 				}
	// 			}
	// 			// else if (selectedDate.value) {
	// 			//   // Backward compatibility: se c'è ancora selectedDate (singola data)
	// 			//   const dateObj = new Date(selectedDate.value);
	// 			//   dateObj.setHours(0, 0, 0, 0);
	// 			//   startTime = Math.floor(dateObj.getTime() / 1000);

	// 			//   const endDateObj = new Date(selectedDate.value);
	// 			//   endDateObj.setHours(23, 59, 59, 999);
	// 			//   endTime = Math.floor(endDateObj.getTime() / 1000);
	// 			// }
	// 		} else {
	// 			// Preset ranges (1h, 24h, 7d, 30d) - usa ora corrente come riferimento
	// 			switch (range) {
	// 				case "1h":
	// 					startTime = endTime - 60 * 60;
	// 					break;
	// 				case "24h":
	// 					// console.log("Calculating startTime for 24h range");
	// 					startTime = endTime - 24 * 60 * 60;
	// 					break;
	// 				case "7d":
	// 					startTime = endTime - 7 * 24 * 60 * 60;
	// 					break;
	// 				case "30d":
	// 					startTime = endTime - 30 * 24 * 60 * 60;
	// 					break;
	// 				default:
	// 					startTime = endTime - 24 * 60 * 60; // default 24h
	// 			}
	// 		}

	// 		// 4. DETERMINA PARAMETRI DOWNSAMPLING
	// 		// Per range lunghi, attiva downsampping automaticamente
	// 		const shouldDownsample = ["7d", "30d", "custom"].includes(range);
	// 		console.log("shoulddownsample:", shouldDownsample);

	// 		// Mappa metriche frontend → backend
	// 		const metricMap: Record<string, string> = {
	// 			speed: "vehicle_speed",
	// 			rpm: "engine_rpm",
	// 			temperature: "engine_temperature",
	// 			fuel: "fuel_level",
	// 			load: "engine_load",
	// 			battery: "tracker_battery",
	// 		};

	// 		const metric = metricMap[selectedMetric.value] || "vehicle_speed";

	// 		// Punti massimi per range (ottimizzati per visualizzazione)
	// 		const maxPointsByRange: Record<string, number> = {
	// 			"1h": 1000, 
	// 			"24h": 2880,
	// 			"7d": 3360, 
	// 			"30d": 3600,
	// 			custom: 1500,
	// 			};

	// 		const maxPoints = maxPointsByRange[range] || 200;

	// 		const params: Record<string, any> = {
	// 			limit: shouldDownsample ? 50000 : 1000,
	// 			vin: selectedVin.value,
	// 			orderBy: "timestamp",
	// 			orderDirection: isSingleDateMode ? "DESC" : "ASC",
	// 			downsample: shouldDownsample ? "true" : "false",
	// 			maxPoints: maxPoints,
	// 			metric: metric,
	// 		};

	// 		// Aggiungi startTime/endTime SEMPRE, anche se sono 0
  //     if (!isSingleDateMode) params.startTime = startTime.toString();
	// 		if (isSingleDateMode) params.strategy = "route";
	// 		// params.startTime = startTime.toString();
	// 		params.endTime = endTime.toString();

	// 		// console.log(`[Frontend DEBUG] Final params:`, params);

	// 		// axios.interceptors.response.use(response => {
	// 		//   console.log('[Axios Response]', {
	// 		//     url: response.config.url,
	// 		//     params: response.config.params,
	// 		//     status: response.status
	// 		//   });
	// 		//   return response;
	// 		// });

	// 		const historicalRes = await axios.get(`/track/${deviceId.value}/logs`, {
	// 			params,
	// 		});

	// 		// console.log(`[TelemetryPage] Risposta API:`, {
	// 		//   success: historicalRes.data?.success,
	// 		//   count: historicalRes.data?.count,
	// 		//   logsLength: historicalRes.data?.logs?.length,
	// 		//   hasData: !!(historicalRes.data?.logs?.length),
	// 		//   range: range,
	// 		// 	startTime: historicalRes.data?.metadata?.startTime,
	// 		// 	endTime: historicalRes.data?.metadata?.endTime,
	// 		// 	downsampled: historicalRes.data?.downsampled || false,
	// 		// });

	// 		// if (historicalRes.data?.logs?.length) {
	// 			// const first = historicalRes.data.logs[0];
	// 			// const last = historicalRes.data.logs[historicalRes.data.logs.length - 1];
	// 			// console.log(`[DEBUG] Statistiche metriche ricevute:`, {
	// 		  // total: historicalRes.data.logs.length,
	// 		  // withMetric: historicalRes.data.logs.filter((l: any) => l[metric] !== null && l[metric] !== undefined).length,
	// 		  // nullMetric: historicalRes.data.logs.filter((l: any) => l[metric] === null || l[metric] === undefined).length,
  // 			//   sampleNullPoints: historicalRes.data.logs
  // 			//     .filter((l: any) => l[metric] === null || l[metric] === undefined)
  // 			//     .slice(0, 3)
  // 			//     .map((l: any) => ({ 
  // 			//       timestamp: new Date(l.timestamp * 1000).toISOString(),
  // 			//       metric: l[metric] 
  // 			//     }))
  // 			// });
	// 			// console.log(`[Frontend DEBUG] Dati ricevuti per il grafico:`, {
  // 			//   total: historicalRes.data.logs.length,
  // 			//   withSpeed: historicalRes.data.logs.filter((l: any) => l.vehicle_speed !== null).length,
  // 			//   dateRange: {
  // 			//     first: historicalRes.data.logs[0] ? 
  // 			//       new Date(historicalRes.data.logs[0].timestamp * 1000).toISOString() : 'N/A',
  // 			//     last: historicalRes.data.logs[historicalRes.data.logs.length - 1] ? 
  // 			//       new Date(historicalRes.data.logs[historicalRes.data.logs.length - 1].timestamp * 1000).toISOString() : 'N/A'
  // 			//   },
  // 			//   // Verifica se ci sono dati del 26 Gennaio
  // 			//   hasJan26Data: historicalRes.data.logs.some((l: any) => 
  // 			//     new Date(l.timestamp * 1000).toISOString().startsWith('2026-01-26')
  // 			//   ),
  // 			//   jan26Data: historicalRes.data.logs
  // 			//     .filter((l: any) => new Date(l.timestamp * 1000).toISOString().startsWith('2026-01-26'))
  // 			//     .slice(0, 3)
  // 			//     .map((l: any) => ({
  // 			//       time: new Date(l.timestamp * 1000).toISOString(),
  // 			//       speed: l.vehicle_speed
  // 			//     }))
  // 			// });
	// 			// console.log(
	// 			//   `[DEBUG] Primo: ${new Date(first.timestamp * 1000).toISOString()}`
	// 			// );
	// 			// console.log(
	// 			//   `[DEBUG] Ultimo: ${new Date(last.timestamp * 1000).toISOString()}`
	// 			// );

	// 			// const daysCovered = (last.timestamp - first.timestamp) / 86400;
	// 			// console.log(`[DEBUG] Giorni coperti: ${daysCovered.toFixed(2)}`);
	// 			// console.log(
	// 			//   `[DEBUG] Downsampled: ${historicalRes.data.downsampled || false}`
	// 			// );
	// 		// }

	// 		// 7. PROCESSO DATI
	// 		if (historicalRes.data?.logs?.length) {
	// 			// Filtra e processa i dati per i grafici
	// 			const filteredHistoricalLogs = historicalRes.data.logs.filter((log: any) => {
	// 				// console.log("filteredhistoricallogs:", filteredHistoricalLogs);
  //         // Accetta dati con la metrica selezionata non nulla
	// 				if (!isSingleDateMode) {
	// 					// const metricValue = log[metric];
	// 					// console.log("log.timestamp e metricValue:", log.timestamp, metricValue);
	// 					return log.timestamp /*&& metricValue !== null && metricValue !== undefined*/;
	// 				}
	// 				return log.timestamp && log.latitude !== null && log.longitude !== null;
	// 			});
	// 			console.log("filteredhistoricallogs dopo:", filteredHistoricalLogs);

	// 			if (filteredHistoricalLogs.length > 0) {
  //         console.log("entro dentro questa condizione di filteredhistoricallogs.length>0");
	// 				// I dati sono già ordinati dal backend, ma assicuriamoci
	// 				const sortedLogs = [...filteredHistoricalLogs].sort((a, b) => {
	// 					const tsA = typeof a.timestamp === "string" ? parseInt(a.timestamp, 10) : a.timestamp;
	// 					const tsB = typeof b.timestamp === "string" ? parseInt(b.timestamp, 10) : b.timestamp;
	// 					return tsA - tsB;
	// 				});

	// 				historicalData.value = sortedLogs;
  //         console.log("historicalData.value dopo il set:", historicalData.value);
	// 				// console.log(
	// 				//   `[DEBUG] Dati pronti per grafico: ${sortedLogs.length} punti`
	// 				// );

	// 				// 8. AGGIORNAMENTO ROTTA (solo se abbiamo dati di posizione)
	// 				// Per la mappa, potremmo volere un downsampling diverso o mantenere più punti
	// 				const newRouteLocations = sortedLogs
	// 					.filter((d: any) => d.latitude && d.longitude)
	// 					.map((d: any) => ({
	// 						lat: d.latitude,
	// 						lng: d.longitude,
	// 						timestamp: d.timestamp,
	// 						speed: d.vehicle_speed,
	// 						rpm: d.engine_rpm,
	// 						fuel: d.fuel_level,
	// 						vin: d.vin,
	// 					}));

	// 				if (newRouteLocations.length > 0) {
	// 					historicalRouteLocations.value = newRouteLocations;

	// 					// Combina: storici + punti live (che non sono già negli storici)
	// 					const freshPoints = getFreshLivePoints();
	// 					routeLocations.value = [...historicalRouteLocations.value, ...freshPoints];

	// 					// console.log(
	// 					//   `[TelemetryPage] Route: ${historicalRouteLocations.value.length} historical + ${freshPoints.length} live points`
	// 					// );

	// 					// Se per la mappa ci sono troppi punti, potremmo applicare un downsampling leggero
	// 					if (routeLocations.value.length > 1000) {
	// 						console.log(`[DEBUG] Route ha ${routeLocations.value.length} punti, applicando downsampling per mappa`);
	// 						// Potresti implementare un downsampling specifico per coordinate
	// 					}
	// 				}
	// 			} else {
	// 				historicalData.value = [];
	// 				// console.warn(`[DEBUG] Nessun dato valido per metrica: ${metric}`);
	// 			}
	// 		} else {
	// 			routeLocations.value = [];
  // 			historicalRouteLocations.value = [];
	// 			// Fallback se l'API non restituisce dati
	// 			console.warn(`[DEBUG] Nessun dato ricevuto dall'API per range: ${range}`);

	// 			// Prova una chiamata più semplice senza downsampling
	// 			try {
	// 				const fallbackRes = await axios.get(`/track/${deviceId.value}/logs`, {
	// 					params: {
	// 						limit: 100,
	// 						vin: selectedVin.value,
	// 						orderBy: "timestamp",
	// 						orderDirection: "ASC",
	// 					},
	// 				});

	// 				if (fallbackRes.data?.logs?.length) {
	// 					historicalData.value = fallbackRes.data.logs;
	// 					// console.log(
	// 					//   `[DEBUG] Fallback: ${fallbackRes.data.logs.length} punti`
	// 					// );
	// 				} else {
	// 					historicalData.value = [];
	// 				}
	// 			} catch (fallbackError) {
	// 				console.error("[DEBUG] Fallback failed:", fallbackError);
	// 				historicalData.value = [];
	// 			}
	// 		}
	// 	} catch (error: any) {
	// 		console.error("[TelemetryPage] Error fetching historical data:", error);

	// 		// Mostra errore specifico per downsampling
	// 		if (error.response?.status === 400 && error.response?.data?.error?.includes("downsample")) {
	// 			toast.error("Errore nel processamento dati, riprova con range più piccolo", {
	// 				position: "top-right",
	// 			});
	// 		} else {
	// 			toast.error("Errore nel caricamento dati storici", {
	// 				position: "top-right",
	// 			});
	// 		}

	// 		historicalData.value = [];
	// 	} finally {
	// 		historicalLoading.value = false;
	// 	}
	// }

	// AGGIUNGI questa funzione:
	// function getFreshLivePoints() {
	//   // Prendi solo i punti molto recenti (ultimi 5 minuti)
	//   const fiveMinutesAgo = Math.floor(Date.now() / 1000) - 300;

	//   if (!routeLocations.value || routeLocations.value.length === 0) return [];

	//   // Filtra i punti già presenti in routeLocations
	//   return routeLocations.value.filter((point) => {
	//     const pointTs =
	//       typeof point.timestamp === "string"
	//         ? parseInt(point.timestamp, 10)
	//         : point.timestamp;

	//     // Solo punti più recenti di 5 minuti E che non sono già in historicalRouteLocations
	//     return (
	//       pointTs > fiveMinutesAgo &&
	//       !historicalRouteLocations.value.some(
	//         (histPoint) => histPoint.timestamp === point.timestamp
	//       )
	//     );
	//   });
	// }

	// function getFreshLivePoints() {
	// 	if (!routeLocations.value || routeLocations.value.length === 0) return [];

	// 	const fiveMinutesAgo = Math.floor(Date.now() / 1000) - 300;

	// 	// Filtra i punti già presenti in routeLocations
	// 	return routeLocations.value.filter((point) => {
	// 		const pointTs = typeof point.timestamp === "string" ? parseInt(point.timestamp, 10) : point.timestamp;

	// 		// Solo punti più recenti di 5 minuti E che non sono già in historicalRouteLocations
	// 		const isFresh = pointTs > fiveMinutesAgo;
	// 		const isNotInHistorical = !historicalRouteLocations.value.some((histPoint) => histPoint.timestamp === point.timestamp);

	// 		return isFresh && isNotInHistorical;
	// 	});
	// }

	// Handler cambio VIN
	// async function onVinChange() {
	// 	if (!selectedVin.value) return;

	// 	routeLocations.value = [];
	// 	historicalRouteLocations.value = [];

	// 	toast.info(`Caricamento dati per VIN: ${selectedVin.value}`, {
	// 		position: "top-right",
	// 		duration: 2000,
	// 	});

	// 	await Promise.all([fetchTelemetryData(), fetchHistoricalData()]);
	// }

	async function onVinChange() {
	  if (!selectedVin.value) return;

	  latestData.value = null;
	  routeLocations.value = [];
	  historicalData.value = [];

	  toast.info(`Caricamento dati per VIN: ${selectedVin.value}`, {
	    position: "top-right",
	    duration: 2000,
	  });

	  await fetchTelemetryData();       // overview / latest
	  await refreshTimeWindow();        // chart + route per finestra corrente
	}

	// Reverse Geocoding (Nominatim)
	async function fetchAddress(lat: number, lon: number) {
		// console.log("Fetching address for coords:", lat, lon);
		try {
			// console.log("Geocoding...", lat, lon);
			const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
			// console.log("Geocoding response:", res);
			const data = await res.json();
			if (data && data.address) {
				// Costruisci stringa indirizzo
				const addr = data.address;
				const parts = [];
				if (addr.road) parts.push(addr.road);
				if (addr.house_number) parts.push(addr.house_number);
				if (addr.postcode) parts.push(addr.postcode);
				if (addr.city || addr.town || addr.village) parts.push(addr.city || addr.town || addr.village);

				currentAddress.value = parts.join(", ");
			} else {
				currentAddress.value = "Indirizzo non trovato";
			}
		} catch (e) {
			console.error("Geocoding error:", e);
			currentAddress.value = "";
		}
	}

	// Formattazione timestamp
	function formatTimestamp(timestamp: number) {
		if (!timestamp) return "N/D";
		return new Date(timestamp * 1000).toLocaleString("it-IT");
	}

	// Classi batteria
	function getBatteryClass(level: number) {
		if (level >= 70) return "high";
		if (level >= 30) return "medium";
		return "low";
	}

	// Controlli mappa
	function centerOnMarker() {
		toast.success("Mappa centrata sulla posizione del veicolo.", {
			position: "top-right",
		});
	}

	function openCoordModal() {
		showCoordModal.value = true;
	}

	function closeCoordModal() {
		showCoordModal.value = false;
	}

	async function copyCoords() {
		if (!currentLocation.value) return;
		const txt = `${currentLocation.value[0].toFixed(6)}, ${currentLocation.value[1].toFixed(6)}`;

		try {
			await navigator.clipboard.writeText(txt);
			toast.success("Coordinate copiate!", { position: "top-right" });
		} catch {
			toast.error("Impossibile copiare le coordinate.", {
				position: "top-right",
			});
		}
	}

	// Controlli grafico - MODIFICATO
	// Range management functions
	// function onDateRangeChange() {
	//   // Auto-sync quando cambiano le date
	//   if (startDate.value && endDate.value) {
	//     chartRange.value = "custom";
	//     fetchHistoricalData("custom");
	//     showChartCalendar.value = false; // Chiudi il popover dopo la selezione
	//   }
	// }

	// function applyDateRange() {
	//   if (!isDateRangeValid.value) {
	//     toast.warning("Seleziona un range valido", { position: "top-right" });
	//     return;
	//   }

	//   chartRange.value = "custom";
	//   fetchHistoricalData("custom");
	//   showChartCalendar.value = false;
	// }

	// function clearDateRange() {
	//   startDate.value = "";
	//   endDate.value = "";
	//   chartRange.value = "24h"; // Torna al default
	//   fetchHistoricalData("24h");
	//   showChartCalendar.value = false;
	// }

	function onCalendarRangeSelect(range: { start: string; end: string }) {
		startDate.value = range.start;
		endDate.value = range.end;
		chartRange.value = "custom";
		// fetchHistoricalData("custom");
		refreshTimeWindow("custom");
		showChartCalendar.value = false;
	}

	// function handleRouteMapDateChange(range: { start: string; end: string }) {
	// 	// console.log('[TelemetryPage] Date range selected in RouteMap:', range);

	// 	// Aggiorna le date nel componente padre
	// 	startDate.value = range.start;
	// 	endDate.value = range.end;
	// 	chartRange.value = "custom";

	// 	// Mostra il calendario (opzionale)
	// 	showChartCalendar.value = false;

	// 	// Fetch nuovi dati per il range selezionato
	// 	// fetchHistoricalData("custom");
	// 	refreshTimeWindow("custom");

	// 	toast.info(`Percorso aggiornato: ${range.start} - ${range.end}`, {
	// 		position: "top-right",
	// 		duration: 3000,
	// 	});
	// }

	// Aggiorna anche setChartRange per cancellare il range quando si seleziona un preset
	// async function setChartRange(range: string) {
	// 	chartRange.value = range;

	// 	if (range === "custom") {
	// 		// Se non c'è range selezionato, mostra il calendario
	// 		if (!startDate.value || !endDate.value) {
	// 			showChartCalendar.value = true;
	// 			return;
	// 		}
	// 	} else {
	// 		// Per preset ranges, reset date
	// 		startDate.value = "";
	// 		endDate.value = "";
	// 	}

	// 	await fetchHistoricalData(range);
	// }

	async function setChartRange(range: string) {
	  chartRange.value = range;

	  if (range !== "custom") {
	    startDate.value = "";
	    endDate.value = "";
	    await refreshTimeWindow(range);
	    return;
	  }

	  // custom
	  if (!startDate.value || !endDate.value) {
	    showChartCalendar.value = true;
	    return;
	  }

	  await refreshTimeWindow("custom");
	}

	// Polling
	let pollInterval: any = null;

	function startPolling() {
		stopPolling();
		pollInterval = setInterval(() => {
			if (selectedVin.value) {
				// Aggiorna solo i dati in tempo reale, non quelli storici
				fetchTelemetryData();
			}
		}, 60000); // Aggiorna ogni 60 secondi
	}

	function stopPolling() {
		if (pollInterval) {
			clearInterval(pollInterval);
			pollInterval = null;
		}
	}

	// Watch per cambiamenti del deviceId
	watch(deviceId, async () => {
		if (deviceId.value) {
			await fetchDeviceData();
		}
	});

	// Lifecycle
	onMounted(() => {
		fetchDeviceData();
		startPolling();
	});

	onBeforeUnmount(() => {
		stopPolling();
	});

	// Computed Status Logic
	const computedStatus = computed(() => {
		if (!latestData.value?.timestamp) return apiStatus.value;

		// Parse timestamp
		const ts = typeof latestData.value.timestamp === "string" ? parseInt(latestData.value.timestamp, 10) : latestData.value.timestamp;

		const now = Math.floor(Date.now() / 1000);
		const diff = now - ts;

		// If older than 15 mins (900 seconds) -> MAINTENANCE (orange)
		if (diff > 900) return "maintenance";

		// If very recent (< 2 mins) -> ONLINE (green)
		if (diff < 120) return "online";

		return apiStatus.value;
	});

	// Helper Functions for Delay
	function getDelayLabel(ts: number | string | undefined): string {
		if (!ts) return "N/D";
		const timestamp = typeof ts === "string" ? parseInt(ts, 10) * 1000 : ts * 1000;
		const diff = Date.now() - timestamp;
		const mins = Math.floor(diff / 60000);

		if (mins < 1) return "Live";
		if (mins < 60) return `${mins}m fa`;
		const hours = Math.floor(mins / 60);
		if (hours < 24) return `${hours}h fa`;
		const days = Math.floor(hours / 24);
		return `${days}gg fa`;
	}

	function getDelayClass(ts: number | string | undefined): string {
		if (!ts) return "";
		const timestamp = typeof ts === "string" ? parseInt(ts, 10) * 1000 : ts * 1000;
		const diff = Date.now() - timestamp;
		// 15 minutes = 900000 ms
		return diff > 900000 ? "warn" : "ok";
	}
</script>

<style scoped>
	/* Header customizations */
	.status-wrapper {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-left: 10px;
	}

	.status-text {
		font-size: 14px;
		font-weight: 500;
		color: #a0a0a0;
		display: inline-flex; /* Changed to inline-flex to keep on same line */
		align-items: center;
		gap: 4px;
	}

	.delay-badge {
		font-size: 13px;
		font-weight: 600;
	}

	.delay-badge.warn {
		color: #fbbf24; /* Amber-400 */
	}

	.delay-badge.ok {
		color: #34d399; /* Emerald-400 */
	}

	.telemetry-container {
		background: #0d0d0d;
		min-height: 100vh;
		color: white;
		font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.telemetric-subtitle {
		font: 800 12px/1 "Inter", system-ui, sans-serif;
		color: rgba(159, 176, 194, 0.92);
		letter-spacing: 0.12em;
		text-transform: uppercase;
		white-space: nowrap;
	}

	/* HEADER PRINCIPALE */
	.main-header {
		background: #1a1a1a;
		border-bottom: 1px solid #333;
		padding: 16px 16px 16px 50px;
	}

	.header-top {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
	}

	.device-info {
		flex: 1;
	}

	.device-title {
		margin: 0 0 6px 0;
		font-size: 24px;
		font-weight: 600;
		color: white;
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.device-subtitle {
		font-size: 14px;
		color: #aaa;
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.device-id {
		background: rgba(255, 255, 255, 0.1);
		padding: 2px 8px;
		border-radius: 4px;
		font-family: monospace;
	}

	.vin-display {
		color: #4caf50;
	}

	.vin-display code {
		background: rgba(76, 175, 80, 0.1);
		padding: 2px 6px;
		border-radius: 4px;
		font-family: monospace;
	}

	.header-actions {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.btn-exit {
		flex: 0 0 auto;
		padding: 8px 16px;
		background: #008f8f;
		border: 0;
		border-radius: 4px;
		color: #fff;
		cursor: pointer;
		font: 600 14px/1 "Inter", sans-serif;
		transition: background 0.15s;
	}

	.btn-exit:hover {
		background: #00a7a7;
	}
	.btn-exit:active {
		background: #007777;
	}

	/* SELEZIONE VIN */
	.vin-selection {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background: rgba(0, 0, 0, 0.2);
		border-radius: 8px;
		padding: 12px 16px;
		border: 1px solid #333;
	}

	.vin-selector {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.vin-label {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 14px;
		color: #aaa;
	}

	.vin-icon {
		font-size: 18px;
	}

	.vin-select {
		background: #222;
		border: 1px solid #555;
		color: white;
		padding: 8px 12px;
		border-radius: 6px;
		min-width: 250px;
		font-size: 14px;
		cursor: pointer;
	}

	.vin-select:focus {
		outline: none;
		border-color: #4caf50;
	}

	.vin-count {
		font-size: 13px;
		color: #888;
		background: rgba(255, 255, 255, 0.05);
		padding: 4px 8px;
		border-radius: 4px;
	}

	.vin-actions {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.btn-refresh {
		background: #333;
		border: 1px solid #555;
		color: white;
		padding: 8px 16px;
		border-radius: 6px;
		cursor: pointer;
		font-size: 14px;
		display: flex;
		align-items: center;
		gap: 6px;
		transition: all 0.2s;
	}

	.btn-refresh:hover:not(:disabled) {
		background: #444;
		border-color: #666;
	}

	.btn-refresh:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.refresh-icon.spinning {
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	.btn-config-charts {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 6px;
		border-radius: 8px;
		border: 1px solid transparent;
		background: transparent;
		color: #e6e6e6;
		cursor: pointer;
		font-size: 18px;
		transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.06s ease;
	}

	.btn-config-charts:hover {
		background: #14181d;
		border-color: #2a2f34;
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
		transform: translateY(-1px);
	}

	.btn-config-charts :deep(svg) {
		width: 18px;
		height: 18px;
		color: #e6e6e6;
	}

	.no-vins-alert {
		background: rgba(255, 193, 7, 0.1);
		border: 1px solid rgba(255, 193, 7, 0.3);
		border-radius: 6px;
		padding: 12px 16px;
		color: #ffc107;
		text-align: center;
		font-size: 14px;
	}

	/* NAVIGATION */
	.mod-nav-sticky {
		position: sticky;
		top: 0;
		z-index: 100; /* Più alto dello z-index dell'header */
		background: #141414;
		border-bottom: 1px solid #333;
		padding: 16px 24px;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
	}

	.mod-nav-container {
		display: flex;
		gap: 8px;
	}

	.mod-tab {
		background: #222;
		border: 1px solid #444;
		color: #aaa;
		padding: 8px 16px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
		transition: all 0.2s;
		flex-shrink: 0; /* Previene lo schiacciamento */
	}

	.mod-tab:hover {
		background: #2a2a2a;
		color: white;
	}

	.mod-tab.active {
		background: #4caf50;
		border-color: #4caf50;
		color: white;
	}

	.telemetry-content {
		padding: 24px;
	}

	.overview-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 20px;
	}

	.card {
		background: #1a1a1a;
		border: 1px solid #333;
		border-radius: 8px;
		padding: 20px;
		transition: all 0.3s ease;
	}

	.card:hover {
		border-color: #444;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	}

	.span-1 {
		grid-column: span 1;
	}
	.span-2 {
		grid-column: span 2;
	}
	.span-3 {
		grid-column: span 3;
	}
	.span-4 {
		grid-column: span 4;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
	}

	.header-right {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	.vin-display {
		font-size: 14px;
		color: #aaa;
	}

	.vin-display strong {
		color: #4caf50;
		font-family: monospace;
	}

	.card-header h3 {
		margin: 0;
		font-size: 18px;
		color: white;
	}

	.live-indicator {
		font-size: 12px;
		font-weight: bold;
		color: #4caf50;
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.dot {
		width: 8px;
		height: 8px;
		background-color: #4caf50;
		border-radius: 50%;
		animation: pulse 2s infinite;
	}

	@keyframes pulse {
		0% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
		}
		70% {
			box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
		}
		100% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
		}
	}

	.status-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		margin: 20px 0 0 0;
		gap: 50px 40px;
	}

	.status-item {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.status-label {
		font-size: 14px;
		color: #aaa;
	}

	.status-value {
		font-size: 24px;
		font-weight: bold;
		color: white;
	}

	.fuel-gauge,
	.battery-gauge {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.fuel-bar {
		flex: 1;
		height: 8px;
		background: #333;
		border-radius: 4px;
		overflow: hidden;
	}

	.fuel-fill {
		height: 100%;
		background: linear-gradient(90deg, #4caf50, #8bc34a);
		border-radius: 4px;
		transition: width 0.3s;
	}

	.fuel-percentage {
		font-size: 14px;
		color: white;
		min-width: 40px;
	}

	.battery-level {
		padding: 4px 12px;
		border-radius: 4px;
		font-size: 14px;
		font-weight: bold;
	}

	.battery-level.high {
		background: #4caf50;
		color: white;
	}
	.battery-level.medium {
		background: #ff9800;
		color: white;
	}
	.battery-level.low {
		background: #f44336;
		color: white;
	}

	.info-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 16px;
	}

	.info-item {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.info-label {
		font-size: 12px;
		color: #aaa;
	}

	.info-value {
		font-size: 16px;
		color: white;
		font-family: monospace;
	}

	.charts-tab,
	.map-tab,
	.stats-tab {
		background: #1a1a1a;
		border: 1px solid #333;
		border-radius: 8px;
		padding: 20px;
		margin-top: 20px;
	}

	.charts-header,
	.map-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
	}

	.chart-controls {
		display: flex;
		gap: 16px;
		align-items: center;
	}

	.metric-select {
		background: #222;
		border: 1px solid #555;
		color: white;
		padding: 8px 12px;
		border-radius: 4px;
		min-width: 150px;
	}

	.range-buttons {
		display: flex;
		gap: 8px;
	}

	.range-buttons button {
		background: #333;
		border: 1px solid #555;
		color: white;
		padding: 6px 12px;
		border-radius: 4px;
		cursor: pointer;
	}

	.range-buttons button:hover {
		background: #444;
	}

	.range-buttons button.active {
		background: #4caf50;
		border-color: #4caf50;
	}

	.short-label {
		display: none;
	}

	.map-info {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	.locations-count {
		font-size: 14px;
		color: #aaa;
	}

	@media (max-width: 1200px) {
		.overview-grid {
			grid-template-columns: repeat(2, 1fr);
		}

		.status-grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media (max-width: 768px) {
		.main-header {
			padding: 12px;
		}

		.header-top {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.vin-selection {
			flex-direction: column;
			gap: 12px;
			align-items: flex-start;
		}

		.vin-selector {
			width: 100%;
			justify-content: space-between;
		}

		.vin-select {
			flex: 1;
		}

		.overview-grid {
			grid-template-columns: 1fr;
		}

		.status-grid {
			grid-template-columns: 1fr;
		}

		.info-grid {
			grid-template-columns: 1fr;
		}

		.charts-header,
		.map-header {
			flex-direction: column;
			gap: 16px;
			align-items: flex-start;
		}

		.chart-controls {
			flex-direction: column;
			width: 100%;
		}

		.metric-select {
			width: 100%;
		}

		.range-buttons {
			width: 100%;
		}

		.range-buttons button {
			flex: 1;
			padding: 8px 4px;
			font-size: 13px;
		}

		.full-label {
			display: none;
		}

		.short-label {
			display: inline;
		}

		.card-hdr {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.header-right {
			flex-direction: column;
			align-items: flex-start;
			gap: 8px;
		}
	}

	/* ───── STACKED LAYOUT STYLES ───── */
	/* .section-block {
    margin-bottom: 24px;
    background: #11161d; /* Match data.css .card */
	/* border-radius: 12px; */
	/* padding: 0;*/ /* Padding handled by internal containers or similar structure */
	/* border: 1px solid #252a32;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
    overflow: hidden;
} */

	/* ───── STACKED LAYOUT STYLES ───── */
	.section-header-main {
		padding: 0 16px;
		margin-top: 24px;
	}

	/* OVERVIEW grid inside section */
	.overview-section .overview-grid {
		padding: 16px;
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 16px;
	}

	/* Specific resets for stacked charts and components */
	.charts-tab {
		background: #11161d;
		border: 1px solid #252a32;
		border-radius: 12px;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
	}

	.charts-header {
		background: #11161d;
		border-bottom: 1px solid #1f242b;
		padding: 12px 16px;
		display: flex;
		justify-content: flex-end; /* Align controls to right */
	}

	.map-component-stacked {
		/* Ensure content inside section block has padding if needed, or flush */
		/* Map usually looks good flush? */
		min-height: 400px;
		height: auto;
		position: relative; /* Ensure z-index context */
		width: 100%;
		border-radius: 0 0 12px 12px; /* Bottom rounded only */
		overflow: hidden;
	}

	.calendar-trigger-btn {
		background: #333;
		color: #fff;
		border: 1px solid #444;
		padding: 6px 12px;
		border-radius: 6px;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 13px;
		transition: all 0.2s;
	}
	.calendar-trigger-btn:hover {
		background: #444;
	}
	.calendar-popover-chart {
		position: absolute;
		top: 40px;
		right: 0;
		z-index: 1000;
	}

	/* Inner content padding for charts/stats */
	.charts-block .chart-container,
	.stats-block .statistics-panel {
		padding: 16px;
	}

	/* Stile per quando è headless */
	.vin-container.headless-vin {
		margin-top: 0;
		padding: 10px 16px;
		background: #1a1a1a;
		border-bottom: 1px solid #333;
	}

	/* Stile normale */
	.vin-container:not(.headless-vin) {
		margin-top: 20px;
		padding: 0 16px;
	}

	.date-range-selector {
		position: relative;
	}

	.date-range-trigger-btn {
		background: #333;
		color: #fff;
		border: 1px solid #444;
		padding: 8px 16px;
		border-radius: 6px;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 14px;
		min-width: 220px;
		justify-content: space-between;
		transition: all 0.2s;
	}

	.date-range-trigger-btn:hover {
		background: #444;
	}

	.range-calendar-container {
		background: #1a1a1a;
		border: 1px solid #333;
		border-radius: 8px;
		padding: 16px;
		width: 350px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
		z-index: 1000;
		position: absolute;
		right: 0;
		top: 40px;
	}

	.range-controls {
		margin-bottom: 16px;
	}

	.date-inputs {
		display: flex;
		gap: 12px;
		margin-bottom: 12px;
	}

	.date-input-group {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.date-input-group label {
		font-size: 12px;
		color: #aaa;
	}

	.date-input {
		background: #222;
		border: 1px solid #444;
		color: white;
		padding: 8px;
		border-radius: 4px;
		font-size: 14px;
	}

	.date-input:focus {
		outline: none;
		border-color: #4caf50;
	}

	.btn-apply-range {
		background: #4caf50;
		color: white;
		border: none;
		padding: 8px 16px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
		margin-right: 8px;
	}

	.btn-apply-range:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.btn-clear-range {
		background: #666;
		color: white;
		border: none;
		padding: 8px 16px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
	}

	.vehicle-logs-wrapper {
  position: relative;
}

.btn-logs {
  background: #333;
  border: 1px solid #555;
  color: white;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-logs:hover:not(:disabled) {
  background: #444;
  border-color: #666;
}

.btn-logs:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.logs-badge {
  background: #008f8f;
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}

.vehicle-logs-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 420px;
  max-height: 360px;
  overflow: hidden;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
}

.vehicle-logs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #333;
  color: white;
  font-weight: 600;
}

.btn-close-logs {
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  font-size: 14px;
}

.vehicle-logs-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.vehicle-log-item {
  padding: 10px 12px;
  border: 1px solid #2d2d2d;
  border-radius: 8px;
  background: #222;
  margin-bottom: 8px;
}

.vehicle-log-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.vehicle-log-vin {
  color: #4caf50;
  font-family: monospace;
  font-size: 13px;
}

.vehicle-log-time {
  color: #aaa;
  font-size: 12px;
  white-space: nowrap;
}

.vehicle-log-text {
  color: white;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
}

.vehicle-logs-empty {
  padding: 16px;
  text-align: center;
  color: #aaa;
}
</style>
